<template>
  <popup-dialog :id="id" class="conflictscreen" title="Speicherkonflikt">
    <div class="p-6">
      <i18n-t
        keypath="errors.modelSynchError"
        tag="p"
        class="conflictscreen__instruction"
        scope="global"
      >
        <div class="conflictscreen__modelname">„{{ localModel.name }}“</div>
      </i18n-t>

      <p class="conflictscreen__instruction">
        {{ $t("messages.chooseModelToKeep") }}
      </p>

      <!-- options -->
      <div>
        <radio-button
          class="conflictscreen__radiobutton"
          :data="('local' as const)"
          :value="chosenVersion"
          @update:value="chosenVersion = $event"
        >
          <template #default="{ selected }">
            <div class="w-full">
              <span
                class="radiobutton__label"
                :class="{ 'is-selected': selected }"
              >
                <template v-if="localModel.deleted">
                  {{ $t("actions.deleteModel") }}
                </template>
                <template v-else>
                  {{ $t("labels.useLocalModel") }}
                </template>
              </span>
              <span class="conflictscreen__actionexplanation">
                {{ $t("labels.overwriteModel") }}
              </span>
            </div>
          </template>
          <template #after>
            <i18n-t
              v-if="!localModel.deleted"
              tag="span"
              keypath="labels.modifiedAtBy"
              class="conflictscreen__time"
              scope="global"
            >
              <span>{{ localModifiedFormatted }}</span>
              <span>{{ $t("labels.byYou") }}</span>
            </i18n-t>
            <span v-else class="conflictscreen__time">
              {{ $t("labels.deleted") }} ({{ localModifiedFormatted }})
            </span>
          </template>
        </radio-button>

        <radio-button
          class="conflictscreen__radiobutton"
          :data="('remote' as const)"
          :value="chosenVersion"
          @update:value="chosenVersion = $event"
        >
          <template #default="{ selected }">
            <div class="w-full">
              <span
                class="radiobutton__label"
                :class="{ 'is-selected': selected }"
              >
                {{ $t("labels.discardChanges") }}
              </span>
              <span class="conflictscreen__actionexplanation">
                {{ $t("labels.useRemoteModel") }}
              </span>
            </div>
          </template>
          <template #after>
            <i18n-t
              v-if="remoteModel && modifier"
              tag="span"
              keypath="labels.modifiedAtBy"
              class="conflictscreen__time"
              scope="global"
            >
              <span>
                {{ remoteModifiedFormatted }}
              </span>
              <span>
                {{ modifier }}
              </span>
            </i18n-t>
            <i18n-t
              v-else-if="remoteModel"
              tag="span"
              keypath="labels.modifiedAt"
              class="conflictscreen__time"
              scope="global"
            >
              {{ remoteModifiedFormatted }}
            </i18n-t>
            <span v-else class="conflictscreen__time">
              {{ $t("labels.deleted") }}
            </span>
          </template>
        </radio-button>

        <radio-button
          class="conflictscreen__radiobutton"
          :data="('both' as const)"
          :value="chosenVersion"
          @update:value="chosenVersion = $event"
        >
          <template #default="{ selected }">
            <div class="w-full">
              <span
                class="radiobutton__label"
                :class="{ 'is-selected': selected }"
              >
                {{ $t("labels.keepBoth") }}
              </span>
              <span class="conflictscreen__actionexplanation">
                {{ $t("labels.createCopyWithNewChanges") }}
              </span>
            </div>
          </template>
        </radio-button>
      </div>

      <i18n-t
        v-if="synchedDate"
        tag="div"
        keypath="labels.lastSynchronisationAt"
        class="conflictscreen__lastsync"
        scope="global"
      >
        {{ synchedDate }}
      </i18n-t>

      <div class="popup__buttons">
        <button
          class="button conflictscreen__button"
          type="button"
          @click="confirm"
        >
          {{ $t("labels.confirm") }}
        </button>
      </div>
    </div>
  </popup-dialog>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { v4 } from "uuid";
import { DateTime } from "luxon";
import { formatDateRelative } from "@/i18n";

import PopupDialog from "@core/ui/PopupDialog.vue";
import RadioButton from "@core/input/RadioButton.vue";
import { useNow } from "@core/composables/useNow";

import type { PropType } from "vue";

export default defineComponent({
  components: {
    PopupDialog,
    RadioButton
  },
  props: {
    id: {
      type: String,
      default: v4()
    },
    localModel: {
      type: Object,
      default: null
    },
    remoteModel: {
      type: Object,
      default: null
    },
    conflictType: {
      type: String as PropType<"data" | "model">,
      default: "model"
    },
    onClose: {
      type: Function,
      default() {
        return function () {};
      }
    }
  },
  emits: ["close"],
  setup() {
    return {
      now: useNow()
    };
  },
  data() {
    return {
      chosenVersion: "both" as "local" | "remote" | "both" // "local" | "remote"
    };
  },
  computed: {
    synchedDate() {
      if (!this.localModel.lastSynch) {
        return null;
      }
      const d = new Date(this.localModel.lastSynch);
      return `${formatDateRelative(d)}`;
    },
    latestRemoteModifiedDate() {
      const infoModified = new Date(this.remoteModel.modified);
      if (!this.remoteModel?.lastVersion?.created) {
        return infoModified;
      }
      const dataModified = new Date(this.remoteModel.lastVersion.created);
      const laterDate = DateTime.max(
        DateTime.fromJSDate(infoModified),
        DateTime.fromJSDate(dataModified)
      );

      return laterDate.toJSDate();
    },
    remoteModified() {
      if (this.remoteModel == null) return null;
      return this.latestRemoteModifiedDate;
    },
    remoteModifiedFormatted() {
      if (this.remoteModel == null) return null;
      console.log("modifiedDate", this.remoteModified);
      const date = this.remoteModified;
      if (date == null) return "-";
      return formatDateRelative(date, { now: this.now });
    },
    localModifiedFormatted() {
      if (this.conflictType == "model") {
        const date = new Date(this.localModel.modified);
        return formatDateRelative(date);
      } else {
        const date = new Date(this.localModel.lastVersion.created);
        return formatDateRelative(date);
      }
    },
    /**
     * The person that changed the remote version of the model:
     */
    modifier() {
      if (this.conflictType == "model") {
        return null;
      } else {
        return this.remoteModel.lastVersion?.creator?.name;
      }
    }
  },
  methods: {
    confirm() {
      this.onClose(this.chosenVersion);
    }
  }
});
</script>

<style lang="scss">
.conflictscreen {
  width: 38rem;
  border-radius: 0.2em;

  &__lastsync,
  &__instruction {
    padding: 1rem;
    text-align: center;
    margin: 0;
  }
  &__lastsync {
    font-size: 13px;
  }
  &__instruction {
    line-height: 1.5;
    &:first-child {
      padding-top: 0;
    }
  }

  &__radiobutton {
    font-size: 1rem;
    font-weight: bold;
    border-bottom: 1px solid var(--color-border);
    padding: 0.75rem 0;
    &:first-of-type {
      border-top: 1px solid var(--color-border);
    }
    & + & {
      margin-top: 0;
    }
  }

  &__actionexplanation {
    font-weight: 400;
  }

  &__modelname {
    font-weight: 700;
  }
  &__time {
    font-size: var(--font-size-label);
    white-space: nowrap;
    font-weight: normal;
    &::first-letter {
      text-transform: capitalize;
    }
  }

  &__button {
    margin: 0.25rem;
  }
}
</style>
